import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/infrastructure/supabase/client'

const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string

export interface AnalyticsInsight {
  summary: string
  recommendations: string[]
  generatedAt: string
  periodDays: number
  cached: boolean
}

// Refreshed at most once/day server-side (see analytics-insights Edge Function) —
// this hook just reads whatever the function currently has cached.
export function useAnalyticsInsights(siteId: string, enabled: boolean, days: number) {
  return useQuery<AnalyticsInsight>({
    queryKey: ['analytics-insights', siteId, days],
    enabled: enabled && !!siteId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/analytics-insights`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ siteId, days }),
      })
      const body = await res.json() as AnalyticsInsight & { error?: string }
      if (!res.ok) throw new Error(body.error ?? `Error ${res.status}`)
      return body
    },
  })
}

export interface PageViewBreakdown {
  page_slug: string
  total_views: number
}

export interface ReferrerBreakdown {
  referrer: string
  total_views: number
}

export interface DailyViewPoint {
  day: string
  total_views: number
}

export function usePageViewBreakdown(siteId: string, enabled: boolean, days = 30) {
  return useQuery<PageViewBreakdown[]>({
    queryKey: ['page-view-breakdown', siteId, days],
    enabled: enabled && !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_page_view_breakdown', { p_site_id: siteId, p_days: days })
      if (error) throw error
      return (data ?? []) as PageViewBreakdown[]
    },
  })
}

export function useReferrerBreakdown(siteId: string, enabled: boolean, days = 30) {
  return useQuery<ReferrerBreakdown[]>({
    queryKey: ['referrer-breakdown', siteId, days],
    enabled: enabled && !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_referrer_breakdown', { p_site_id: siteId, p_days: days })
      if (error) throw error
      return (data ?? []) as ReferrerBreakdown[]
    },
  })
}

export function useDailyViewChart(siteId: string, enabled: boolean, days = 30) {
  return useQuery<DailyViewPoint[]>({
    queryKey: ['daily-view-chart', siteId, days],
    enabled: enabled && !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_daily_view_chart', { p_site_id: siteId, p_days: days })
      if (error) throw error
      return (data ?? []) as DailyViewPoint[]
    },
  })
}

export function useTotalPageCount() {
  return useQuery({
    queryKey: ['pages-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('pages')
        .select('id', { count: 'exact', head: true })
      if (error) throw error
      return count ?? 0
    },
  })
}

export function usePageViewCount(enabled: boolean) {
  return useQuery({
    queryKey: ['page-views-count'],
    enabled,
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const { count, error } = await supabase
        .from('page_views')
        .select('id', { count: 'exact', head: true })
        .gte('viewed_at', since)
      if (error) throw error
      return count ?? 0
    },
  })
}

export function useSiteViewStats(enabled: boolean) {
  return useQuery({
    queryKey: ['site-view-stats'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_site_view_stats', { p_days: 30 })
      if (error) throw error
      return Object.fromEntries(
        ((data ?? []) as { site_id: string; total_views: number }[])
          .map(({ site_id, total_views }) => [site_id, total_views])
      ) as Record<string, number>
    },
  })
}
